/** Endpoint run routing reconstructed from authenticated command receipts, never relay projections. */
import type { SignedCommand } from '@viko/crypto'
import { COMMAND_PAYLOADS, deriveRunTitle, EncryptedContent } from '@viko/protocol'
import { idb } from './idb.js'
import type { CommandOutcome, RunSummary } from './types.js'

type Receipt = { command: SignedCommand; outcome: CommandOutcome }
const storageKey = (account: string) => `encrypted-run-receipts/v1/${account}`
export class BrowserRuns {
  private locked = false
  /** read-budget: receipts are immutable ciphertext; each projects (decrypts) once per session. */
  private readonly projected = new Map<string, Promise<RunSummary | undefined>>()
  constructor(
    readonly accountId: string,
    private readonly decode: (
      command: SignedCommand,
      out: CommandOutcome,
    ) => Promise<CommandOutcome>,
    private readonly request: (command: SignedCommand) => Promise<unknown>,
  ) {}
  lock() {
    this.locked = true
    this.projected.clear()
  }
  async forget() {
    this.lock()
    await idb.del(storageKey(this.accountId))
  }
  private check() {
    if (this.locked) throw new Error('Encrypted runs locked')
  }
  private async project(receipt: Receipt): Promise<RunSummary | undefined> {
    this.check()
    const { command, outcome } = receipt
    if (command.envelope.kind !== 'run.start') return
    const sent = EncryptedContent.parse(command.payload)
    if (sent.context.accountId !== this.accountId) throw new Error('Run receipt account mismatch')
    const opened = await this.decode(command, outcome)
    this.check()
    if (opened.status !== 'done') return
    const result = opened.result as Record<string, unknown> | undefined
    if (!result || typeof result.runId !== 'string' || typeof result.status !== 'string')
      throw new Error('Invalid encrypted run receipt')
    const start = COMMAND_PAYLOADS['run.start'].parse(await this.request(command))
    this.check()
    if (start.runId !== result.runId) throw new Error('Run receipt request mismatch')
    const row: RunSummary = {
      runId: result.runId,
      machineId: command.envelope.machineId,
      status: result.status,
      headSeq: 0,
      agent: start.agent,
      title:
        deriveRunTitle({
          taskTitle: typeof result.taskTitle === 'string' ? result.taskTitle : undefined,
          prompt: start.prompt,
        }).title || 'New session',
      prompt: start.prompt,
      ...(typeof result.taskTitle === 'string' ? { taskTitle: result.taskTitle } : {}),
      ...(start.model ? { model: start.model } : {}),
      ...(start.accountId ? { accountId: start.accountId } : {}),
    }
    for (const field of [
      'cwd',
      'projectId',
      'workstreamId',
      'taskId',
      'worktree',
      'branch',
    ] as const)
      if (typeof result[field] === 'string') row[field] = result[field]
    return row
  }
  async remember(command: SignedCommand, outcome: CommandOutcome) {
    if (command.envelope.kind !== 'run.start' || outcome.status !== 'done') return
    // Persist only ciphertext and its original signed envelope. Verify before indexing anything.
    EncryptedContent.parse(
      typeof outcome.result === 'string' ? JSON.parse(outcome.result) : outcome.result,
    )
    const receipt = {
      command,
      outcome: { commandId: outcome.commandId, status: outcome.status, result: outcome.result },
    }
    const row = await this.project(receipt)
    if (!row) return
    this.check()
    await idb.update<Record<string, Receipt>>(storageKey(this.accountId), (saved) => {
      this.check()
      return { ...saved, [command.envelope.id]: receipt }
    })
  }
  async list() {
    this.check()
    const saved = (await idb.get<Record<string, Receipt>>(storageKey(this.accountId))) ?? {}
    const rows = new Map<string, RunSummary>()
    for (const [id, receipt] of Object.entries(saved)) {
      let p = this.projected.get(id)
      if (!p) {
        p = this.project(receipt)
        this.projected.set(id, p)
        p.catch(() => this.projected.delete(id))
      }
      const row = await p
      if (row) rows.set(row.runId, row)
    }
    this.check()
    return [...rows.values()]
  }
}
